import React, { useState, useContext, useEffect, useRef } from "react";
import { Switch, Grid, theme as antdTheme } from "antd";
import {
  UserOutlined, LogoutOutlined, IdcardOutlined, HomeOutlined,
  QrcodeOutlined, TrophyOutlined, ShoppingOutlined, SettingOutlined,
  TeamOutlined, ScheduleOutlined, MessageOutlined, GiftOutlined,
  SunOutlined, MoonOutlined, NotificationOutlined, MenuOutlined,
  CloseOutlined, ArrowRightOutlined,
  CalendarOutlined, FireOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { getBackendUrl } from "../utils/env";
import { ThemeContext } from "../context/ThemeContext";
import { getMenuSettings } from "../services/settingsService";

const { useBreakpoint } = Grid;

const bravoColor = "#ffe000";

/* ─── LIQUID GLASS STYLES ─────────────────────────────────────────────────── */
const makeStyles = (t, isDark) => `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Mulish:wght@400;500;600&display=swap');

  /* ── Variables de vidrio ── */
  :root {
    --glass-bg-light:   rgba(255, 255, 255, 0.55);
    --glass-bg-dark:    rgba(18, 20, 28, 0.55);
    --glass-border-l:   rgba(255, 255, 255, 0.70);
    --glass-border-d:   rgba(255, 255, 255, 0.10);
    --glass-shine-l:    rgba(255, 255, 255, 0.80);
    --glass-shine-d:    rgba(255, 255, 255, 0.14);
    --glass-blur:       20px;
    --glass-sat:        180%;
    --pill-bg-l:        rgba(255, 255, 255, 0.50);
    --pill-bg-d:        rgba(255, 255, 255, 0.08);
    --pill-border-l:    rgba(255, 255, 255, 0.75);
    --pill-border-d:    rgba(255, 255, 255, 0.14);
  }

  /* ── Barra ── */
  .nb-bar {
    font-family: 'Mulish', sans-serif;
    position: fixed;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% - 32px);
    max-width: 1100px;
    height: 54px;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px 0 20px;
    border-radius: 18px;

    /* Vidrio */
    background: ${isDark ? "var(--glass-bg-dark)" : "var(--glass-bg-light)"};
    backdrop-filter: blur(var(--glass-blur)) saturate(var(--glass-sat));
    -webkit-backdrop-filter: blur(var(--glass-blur)) saturate(var(--glass-sat));

    /* Borde especular */
    border: 1px solid ${isDark ? "var(--glass-border-d)" : "var(--glass-border-l)"};

    /* Sombra con color del tema */
    box-shadow:
      0 8px 32px rgba(0,0,0,${isDark ? "0.40" : "0.12"}),
      0 1px 0 ${isDark ? "var(--glass-shine-d)" : "var(--glass-shine-l)"} inset,
      0 -1px 0 rgba(0,0,0,0.06) inset;

    transition: box-shadow 0.3s, background 0.3s;
  }

  /* Línea brillante superior (efecto especular) */
  .nb-bar::before {
    content: '';
    position: absolute;
    top: 0; left: 16px; right: 16px;
    height: 1px;
    background: ${isDark
    ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)"
    : "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.90) 50%, transparent 100%)"};
    border-radius: 1px;
    pointer-events: none;
  }

  /* Reflejo de luz inferior difuso */
  .nb-bar::after {
    content: '';
    position: absolute;
    bottom: -1px; left: 20%; right: 20%;
    height: 1px;
    background: ${isDark
    ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)"
    : "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)"};
    pointer-events: none;
  }

  /* ── Logo ── */
  .nb-logo {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    cursor: pointer;
    user-select: none;
  }
  .nb-logo img { height: 28px; object-fit: contain; display: block; }

  /* ── Nav links escritorio ── */
  .nb-links {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 1;
    justify-content: center;
    padding: 0 20px;
  }
  .nb-link {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 11px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    color: ${isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.55)"};
    text-decoration: none;
    transition: all 0.18s;
    white-space: nowrap;
    position: relative;
  }
  .nb-link:hover {
    color: ${isDark ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.85)"};
    background: ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"};
  }
  .nb-link.active {
    color: ${bravoColor};
    background: ${isDark
    ? `rgba(${hexToRgb(bravoColor)}, 0.18)`
    : `rgba(${hexToRgb(bravoColor)}, 0.10)`};
    box-shadow: 0 0 0 1px ${isDark
    ? `rgba(${hexToRgb(bravoColor)}, 0.25)`
    : `rgba(${hexToRgb(bravoColor)}, 0.20)`} inset;
  }
  .nb-link-icon { font-size: 13px; }

  /* ── Pill de usuario (vidrio secundario) ── */
  .nb-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 5px 4px 10px;
    border-radius: 100px;
    background: ${isDark ? "var(--pill-bg-d)" : "var(--pill-bg-l)"};
    border: 1px solid ${isDark ? "var(--pill-border-d)" : "var(--pill-border-l)"};
    box-shadow: ${isDark
    ? "0 2px 8px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.06) inset"
    : "0 2px 8px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.9) inset"};
    flex-shrink: 0;
  }

  /* ── Avatar ── */
  .nb-avatar-btn {
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    border-radius: 50%;
    display: block;
    line-height: 0;
  }
  .nb-avatar-img {
    width: 30px; height: 30px;
    border-radius: 50%;
    object-fit: cover;
    border: 1.5px solid ${isDark ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.90)"};
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    display: block;
  }
  .nb-avatar-placeholder {
    width: 30px; height: 30px;
    border-radius: 50%;
    background: ${isDark
    ? `rgba(${hexToRgb(bravoColor)}, 0.25)`
    : `rgba(${hexToRgb(bravoColor)}, 0.15)`};
    border: 1.5px solid ${isDark ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.90)"};
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 700;
    color: ${bravoColor};
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  }

  /* ── Dropdown (vidrio profundo) ── */
  .nb-dropdown {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    background: ${isDark
    ? "rgba(22, 25, 34, 0.82)"
    : "rgba(255, 255, 255, 0.82)"};
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border: 1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.80)"};
    border-radius: 16px;
    box-shadow:
      0 16px 40px rgba(0,0,0,${isDark ? "0.5" : "0.16"}),
      0 1px 0 ${isDark ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.95)"} inset;
    overflow: hidden;
    min-width: 192px;
    z-index: 9999;
    animation: ddIn 0.18s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
  @keyframes ddIn {
    from { opacity: 0; transform: translateY(-8px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .nb-dd-header {
    padding: 14px 16px 11px;
    border-bottom: 1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"};
  }
  .nb-dd-name {
    font-family: 'Syne', sans-serif;
    font-size: 14px; font-weight: 700;
    color: ${isDark ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.85)"};
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .nb-dd-role {
    font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; margin-top: 1px;
    color: ${isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)"};
  }
  .nb-dd-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px;
    font-size: 13px; font-weight: 500;
    color: ${isDark ? "rgba(255,255,255,0.80)" : "rgba(0,0,0,0.75)"};
    cursor: pointer; transition: background 0.12s;
    border: none; background: none; width: 100%; text-align: left;
    font-family: 'Mulish', sans-serif;
  }
  .nb-dd-item:hover {
    background: ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"};
  }
  .nb-dd-item.danger { color: ${t.colorError}; }
  .nb-dd-item.danger:hover { background: ${t.colorErrorBg}; }
  .nb-dd-divider {
    height: 1px;
    background: ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"};
    margin: 3px 0;
  }

  /* ── Hamburguesa ── */
  .nb-burger {
    display: flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border-radius: 10px;
    background: ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"};
    border: 1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"};
    cursor: pointer;
    color: ${isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)"};
    font-size: 16px; flex-shrink: 0; transition: all 0.15s;
  }
  .nb-burger:hover {
    background: ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"};
  }

  /* ── Drawer overlay ── */
  .nb-drawer-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,${isDark ? "0.55" : "0.30"});
    backdrop-filter: blur(4px);
    z-index: 1099;
    animation: overlayIn 0.2s ease;
  }
  @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

  /* ── Drawer (vidrio profundo) ── */
  .nb-drawer {
    position: fixed;
    top: 10px; right: 10px; bottom: 10px;
    width: min(290px, 86vw);
    background: ${isDark
    ? "rgba(18, 21, 30, 0.88)"
    : "rgba(255, 255, 255, 0.88)"};
    backdrop-filter: blur(28px) saturate(180%);
    -webkit-backdrop-filter: blur(28px) saturate(180%);
    border: 1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.85)"};
    border-radius: 22px;
    box-shadow:
      0 24px 60px rgba(0,0,0,${isDark ? "0.55" : "0.18"}),
      0 1px 0 ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,1.0)"} inset;
    z-index: 1100;
    display: flex; flex-direction: column;
    animation: drawerIn 0.24s cubic-bezier(0.34, 1.4, 0.64, 1);
    overflow: hidden;
  }
  @keyframes drawerIn {
    from { opacity: 0; transform: translateX(32px) scale(0.97); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
  }

  .nb-drawer-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"};
    flex-shrink: 0;
  }
  .nb-drawer-user {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"};
    flex-shrink: 0;
  }
  .nb-drawer-avatar {
    width: 42px; height: 42px; border-radius: 50%;
    object-fit: cover;
    border: 2px solid ${isDark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.90)"};
    box-shadow: 0 3px 10px rgba(0,0,0,0.2);
    flex-shrink: 0;
  }
  .nb-drawer-avatar-ph {
    width: 42px; height: 42px; border-radius: 50%;
    background: ${isDark
    ? `rgba(${hexToRgb(bravoColor)}, 0.22)`
    : `rgba(${hexToRgb(bravoColor)}, 0.12)`};
    border: 2px solid ${isDark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.90)"};
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; font-weight: 700; color: ${bravoColor};
    flex-shrink: 0;
  }
  .nb-drawer-user-name {
    font-family: 'Syne', sans-serif;
    font-size: 15px; font-weight: 700; line-height: 1.2;
    color: ${isDark ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.85)"};
  }
  .nb-drawer-user-role {
    font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; margin-top: 1px;
    color: ${isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)"};
  }

  .nb-drawer-group-label {
    font-size: 10px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
    color: ${isDark ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.30)"};
    padding: 14px 18px 6px;
  }

  .nb-drawer-links { flex: 1; overflow-y: auto; padding-bottom: 8px; }

  .nb-drawer-link {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 18px;
    font-size: 14px; font-weight: 500;
    color: ${isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.70)"};
    text-decoration: none; transition: background 0.12s;
    border-left: 3px solid transparent;
    margin: 1px 0;
  }
  .nb-drawer-link:hover {
    background: ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"};
  }
  .nb-drawer-link.active {
    border-left-color: ${bravoColor};
    background: ${isDark
    ? `rgba(${hexToRgb(bravoColor)}, 0.14)`
    : `rgba(${hexToRgb(bravoColor)}, 0.08)`};
    color: ${bravoColor};
    font-weight: 600;
  }
  .nb-drawer-link-left { display: flex; align-items: center; gap: 11px; }
  .nb-drawer-link-icon {
    width: 30px; height: 30px; border-radius: 8px;
    background: ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"};
    display: flex; align-items: center; justify-content: center;
    font-size: 14px;
    color: ${isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)"};
    flex-shrink: 0;
  }
  .nb-drawer-link.active .nb-drawer-link-icon {
    background: ${isDark
    ? `rgba(${hexToRgb(bravoColor)}, 0.20)`
    : `rgba(${hexToRgb(bravoColor)}, 0.12)`};
    color: ${bravoColor};
  }

  .nb-drawer-footer {
    border-top: 1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"};
    padding: 12px 18px;
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
  }
  .nb-drawer-logout {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 600; color: ${t.colorError};
    cursor: pointer; background: none; border: none;
    font-family: 'Mulish', sans-serif; padding: 0;
    transition: opacity 0.15s;
  }
  .nb-drawer-logout:hover { opacity: 0.70; }

  /* ── Switch personalizado ── */
  .nb-switch-wrap .ant-switch {
    background: ${isDark
    ? `rgba(${hexToRgb(bravoColor)}, 0.60)`
    : "rgba(0,0,0,0.18)"} !important;
  }
  .nb-switch-wrap .ant-switch-checked {
    background: ${bravoColor} !important;
  }

  /* ── Espaciador ── */
  
  /* ────────────────────────────── */
  /* LIQUID BOTTOM NAV */
  /* ────────────────────────────── */

  .liquid-nav {
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border-radius: 999px;
    background: ${isDark ? "rgba(25,25,30,.65)" : "rgba(255,255,255,.65)"};
    backdrop-filter: blur(32px) saturate(180%);
    -webkit-backdrop-filter: blur(32px) saturate(180%);
    border: 1px solid ${isDark ? "rgba(255,255,255,.10)" : "rgba(255,255,255,.80)"};
    box-shadow: 0 10px 35px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.25);
    z-index: 9999;
  }

  .liquid-item {
    display:flex;
    align-items:center;
    justify-content:center;
    width:46px;
    height:46px;
    border-radius:999px;
    text-decoration:none;
    transition:all .35s cubic-bezier(.22,1,.36,1);
    color:${isDark ? "rgba(255,255,255,.65)" : "rgba(0,0,0,.60)"};
  }

  .liquid-item.active{
    width:auto;
    padding:0 16px;
    gap:8px;
    color:${bravoColor};
    background:${isDark ? `rgba(${hexToRgb(bravoColor)}, .25)` : `rgba(${hexToRgb(bravoColor)}, .12)`};
  }

  .liquid-label{
    font-size:12px;
    font-weight:600;
    white-space:nowrap;
  }
  .ignis-link {
  color: #ff4d4f !important;
}

  @media (min-width:768px){
    .liquid-nav{display:none;}
  }


  .nb-spacer { height: 74px; }
`;

/* ─── Helper hex → rgb ────────────────────────────────────────────────────── */
function hexToRgb(hex = "#000000") {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return "0,0,0";
  return `${r},${g},${b}`;
}

/* ─── NAVBAR ──────────────────────────────────────────────────────────────── */
export default function Navbar() {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { token } = antdTheme.useToken();
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [menuSettings, setMenuSettings] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);


  const user = auth?.user;
  const isAdmin = user?.id === 1;

  useEffect(() => {
    if (!auth || isAdmin) return;

    const loadMenuSettings = async () => {
      try {
        const data = await getMenuSettings();
        setMenuSettings(data);
      } catch (error) {
        console.error("Error cargando configuración del menú", error);
      }
    };

    loadMenuSettings();
  }, [auth, isAdmin]);

  if (!auth) return null;

  const { logout } = auth;


  const isDark = theme === "dark";
  const css = makeStyles(token, isDark);

  const avatarSrc = user?.profile_picture
    ? `${getBackendUrl()}${user.profile_picture}`
    : null;

  const userName = user?.full_name || user?.name || "Usuario";
  const userInitial = userName[0]?.toUpperCase() ?? "?";
  const isActive = (path) => location.pathname === path;

  const userLinks = [
    { key: "home", icon: <HomeOutlined />, label: "Inicio", path: "/dashboard" },
    { key: "scan", icon: <QrcodeOutlined />, label: "Escaneo", path: "/scan" },
    { key: "ranking", icon: <TrophyOutlined />, label: "Ranking", path: "/ranking" },
    { key: "store", icon: <ShoppingOutlined />, label: "Tienda", path: "/store" },
    { key: "birthdays", icon: <GiftOutlined />, label: "Cumpleaños", path: "/birthdays" },
    { key: "events", icon: <CalendarOutlined />, label: "Eventos", path: "/events" },
    { key: "feedback", icon: <MessageOutlined />, label: "Comentarios", path: "/feedback" },
    { key: "IGNIS", icon: <FireOutlined />, label: "IGNIS", path: "/ignis" },
  ];

  const adminLinks = [
    { key: "admin-panel", icon: <SettingOutlined />, label: "Panel", path: "/admin" },
    { key: "admin-qr", icon: <QrcodeOutlined />, label: "QR", path: "/admin/qr" },
    { key: "admin-users", icon: <TeamOutlined />, label: "Dinámicos", path: "/admin/users" },
    { key: "admin-act", icon: <ScheduleOutlined />, label: "Actividades", path: "/admin/activities" },
    { key: "admin-store", icon: <ShoppingOutlined />, label: "Productos", path: "/admin/store" },
    { key: "admin-events", icon: <CalendarOutlined />, label: "Eventos", path: "/admin/events" },
    { key: "admin-notif", icon: <NotificationOutlined />, label: "Notificaciones", path: "/admin/notifications" },
    { key: "admin-fb", icon: <MessageOutlined />, label: "Comentarios", path: "/admin/feedback" },
  ];

  const visibleLinks = isAdmin
    ? adminLinks
    : userLinks.filter(link => menuSettings[link.key] !== false);
  const desktopLinks = visibleLinks

  const handleLogout = () => {
    setDrawerOpen(false);
    setDropdownOpen(false);
    logout();
    navigate("/");
  };


  return (
    <>
      <style>{css}</style>

      {/* ── BARRA ── */}
      <nav className="nb-bar">

        {/* Logo */}
        <div className="nb-logo" onClick={() => navigate("/")}>
          <img
            src={isDark ? "/img/logo-blanco.png" : "/img/logo-negro.png"}
            alt="Logo"
          />
        </div>

        {/* Links escritorio */}
        {screens.md && (
          <div className="nb-links">
            {desktopLinks.map((l) => (
              <Link
                key={l.key}
                to={l.path}
                className={`nb-link${isActive(l.path) ? " active" : ""}`}
              >
                <span className="nb-link-icon">{l.icon}</span>
                {l.label}
              </Link>
            ))}
          </div>
        )}

        {/* Pill derecha */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="nb-pill">
            {/* Switch tema */}
            <div className="nb-switch-wrap">
              <Switch
                checked={isDark}
                onChange={toggleTheme}
                checkedChildren={<MoonOutlined style={{ fontSize: 11 }} />}
                unCheckedChildren={<SunOutlined style={{ fontSize: 11 }} />}
                style={{ backgroundColor: isDark ? token.colorPrimary : undefined }}
              />
            </div>

            {/* Avatar + dropdown */}
            <div
              ref={dropdownRef}
              className="nb-avatar-btn"
              onClick={() => setDropdownOpen((v) => !v)}
            >
              {avatarSrc
                ? <img src={avatarSrc} alt={userName} className="nb-avatar-img" />
                : <div className="nb-avatar-placeholder">{userInitial}</div>
              }

              {dropdownOpen && (
                <>
                  <div
                    style={{ position: "fixed", inset: 0, zIndex: 9998 }}
                    onClick={(e) => { e.stopPropagation(); setDropdownOpen(false); }}
                  />
                  <div className="nb-dropdown" onClick={(e) => e.stopPropagation()}>
                    <div className="nb-dd-header">
                      <div className="nb-dd-name">{userName}</div>
                      <div className="nb-dd-role">{isAdmin ? "Administrador" : "Miembro"}</div>
                    </div>
                    <button
                      className="nb-dd-item"
                      onClick={() => { navigate("/perfil"); setDropdownOpen(false); }}
                    >
                      <IdcardOutlined /> Perfil
                    </button>
                    <div className="nb-dd-divider" />
                    <button className="nb-dd-item danger" onClick={handleLogout}>
                      <LogoutOutlined /> Cerrar sesión
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Hamburguesa */}

        </div>
      </nav>

      {/* ── DRAWER MÓVIL ── */}
      {drawerOpen && (
        <>
          <div className="nb-drawer-overlay" onClick={() => setDrawerOpen(false)} />
          <div className="nb-drawer">

            {/* Cabecera */}
            <div className="nb-drawer-head">
              <img
                src={isDark ? "/img/logo-blanco.png" : "/img/logo-negro.png"}
                height={24} alt="Logo"
              />
              <div
                className="nb-burger"
                style={{ width: 30, height: 30 }}
                onClick={() => setDrawerOpen(false)}
              >
                <CloseOutlined style={{ fontSize: 13 }} />
              </div>
            </div>

            {/* Usuario */}
            <div className="nb-drawer-user">
              {avatarSrc
                ? <img src={avatarSrc} alt={userName} className="nb-drawer-avatar" />
                : <div className="nb-drawer-avatar-ph">{userInitial}</div>
              }
              <div>
                <div className="nb-drawer-user-name">{userName}</div>
                <div className="nb-drawer-user-role">{isAdmin ? "Administrador" : "Miembro"}</div>
              </div>
            </div>

            {/* Links */}
            <div className="nb-drawer-group-label">
              {isAdmin ? "Administración" : "Navegación"}
            </div>
            <div className="nb-drawer-links">
              {visibleLinks.map((l) => (

                <Link
                  key={l.key}
                  to={l.path}
                  className={`nb-drawer-link${isActive ? " active" : ""}`}
                  onClick={() => setDrawerOpen(false)}
                >
                  {React.cloneElement(l.icon, {
                    ...l.icon.props,
                    style: {
                      ...l.icon.props.style,
                      color: l.label === "IGNIS" && !isActive ? "nb-dd-item danger" : "inherit",
                    },
                  })}

                  <span style={{ color: l.label === "IGNIS" ? "nb-dd-item danger" : "inherit" }}>
                    {l.label}
                  </span>

                  <ArrowRightOutlined style={{ fontSize: 10, opacity: 0.35 }} />
                </Link>
              ))}
            </div>

            {/* Footer */}
            <div className="nb-drawer-footer">
              <button className="nb-drawer-logout" onClick={handleLogout}>
                <LogoutOutlined /> Cerrar sesión
              </button>
              <Switch
                checked={isDark}
                onChange={toggleTheme}
                checkedChildren={<MoonOutlined style={{ fontSize: 11 }} />}
                unCheckedChildren={<SunOutlined style={{ fontSize: 11 }} />}
                style={{ backgroundColor: isDark ? token.colorPrimary : undefined }}
              />
            </div>
          </div>
        </>
      )}


      {!screens.md && (
        <div className="liquid-nav">
          {visibleLinks.slice(0, 6).map((l) => (
            <Link
              key={l.key}
              to={l.path}
              className={`liquid-item${isActive(l.path) ? " active" : ""}`}
            >
              {l.icon}
              {isActive(l.path) && (
                <span className="liquid-label">{l.label}</span>
              )}
            </Link>
          ))}
        </div>
      )}

      <div className="nb-spacer" />
    </>
  );
}
